import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import {applyTableWidths, setTheadTBodyHeight} from "../Model/TableModel";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 */

const CustomTableApp = ({
    tableId,
    searchId,
    checkbox,
    thead,
    tbody,
    inModal,
    textNotInformation ,
    childrenNotInformation,
    withoutHeight
}) => {

    const elementRef = useRef(null);
    const tbodyRef = useRef(null);
    const rafRef = useRef(null);
    const { t } = useTranslation();
    const [emptyLegend, setEmptyLegend] = useState('loading');
    const rows = Array.isArray(tbody) ? tbody : (tbody?.body || []);
    const isSearchEmpty = emptyLegend === 'coincidences_not_found';

    /**
     * Executes column width calculation and applies the result to the table.
     *
     * Delegates the logic to `applyTableWidths`, ensuring column widths
     * are adjusted according to content and layout.
     *
     * @returns {void}
     */
    const applyColumnWidths = useCallback(() => {
        applyTableWidths({ tableId, rows, checkbox, thead });
    }, [tableId, thead, rows]);

    /**
     * Handles layout initialization and column width calculation after render.
     *
     * - Applies dynamic positioning using `setTheadTBodyHeight`
     * - Uses requestAnimationFrame to defer width calculation
     *   until the DOM layout is stable
     *
     * Dependencies:
     * - applyColumnWidths: recalculated if table structure changes
     * - inModal: affects layout positioning
     * - withoutHeight: controls height behavior
     */
    useLayoutEffect(() => {
        setTheadTBodyHeight({withoutHeight, elementRef, tbodyRef, inModal});

        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            applyColumnWidths();
        });

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [applyColumnWidths, inModal, withoutHeight]);

    /**
     * Observes container size changes and recalculates column widths when needed.
     *
     * - Adjusts table height dynamically when `withoutHeight` is enabled
     * - Reapplies column widths on resize events
     *
     * @returns {void}
     */
    useEffect(() => {
        if (!elementRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            const table = document.getElementById(tableId);

            if (withoutHeight === false && table && elementRef.current) {
                const rect = elementRef.current.getBoundingClientRect();
                table.style.height = `${rect.height}px`;
            }

            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                applyColumnWidths();
            });
        });

        resizeObserver.observe(elementRef.current);

        return () => resizeObserver.disconnect();
    }, [withoutHeight, tableId, applyColumnWidths]);

    /**
     * Determines the empty state of the table based on:
     * - Presence of rows
     * - Current search input value
     *
     * Updates the `emptyLegend` state accordingly.
     *
     * @returns {void}
     */
    useEffect(() => {
        const searchElement = searchId ? document.getElementById(searchId) : null;

        if (rows.length === 0) {
            if (searchElement?.value?.length > 0) {
                setEmptyLegend('coincidences_not_found');
            } else {
                setEmptyLegend('no_information');
            }
        }
    }, [rows, searchId]);

    /**
     * Computes the final text to display when the table is empty.
     *
     * Logic:
     * - If search yields no results → no text (handled externally)
     * - If data exists → no text
     * - If custom empty component exists → no text
     * - If custom text is provided → use it
     * - Otherwise → fallback to default translation key
     *
     * @type {string}
     */
    const finalText = (() => {
        if (isSearchEmpty) return '';
        if (rows.length > 0) return '';
        if (Boolean(childrenNotInformation) && emptyLegend === 'no_information') return '';
        if (typeof textNotInformation === 'string' && textNotInformation.trim() !== '') {
            return t(textNotInformation);
        }
        return t(emptyLegend);
    })();

    return  {
        t,
        elementRef,
        tbodyRef,
        finalText,
        isSearchEmpty
    }
};

export default CustomTableApp;
